import { createSlice } from '@reduxjs/toolkit';
import AppError from '../../models/error';
import { setCurrentUser, clearCurrentUser, clearNewUser } from './userSlice';
import { AuthenticationType } from '../../enums/authentication-type';
import { UserType } from '../../enums/user-type';
import axios from '../../config/axios-config';
import { setApiLoading, setApiSuccess, setApiError, ApiStatusKeys } from './apiStatusSlice';
import { signOut } from '../../services/authService';
import TokenService from '../../services/tokenService';
import { TokenType } from '../../enums/token-type';
import { handleApiError, handleError } from '../../helpers/errorHelper';

const initialState = {
  isAuthenticated: false,
  authenticationType: AuthenticationType.Basic,
  token: null,
  isUserExist: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      state.isAuthenticated = true;
      state.token = null;
      state.authenticationType = action.payload.type;
      TokenService.setToken(TokenType.User, action.payload.token);
    },
    registerSuccess: (state, action) => {
      state.isAuthenticated = true;
      state.token = null;
      state.authenticationType = action.payload.type;
      TokenService.setToken(TokenType.User, action.payload.token);
    },

    registerFailed: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      TokenService.removeToken();
    },

    loginFailure: (state) => {
      state.isAuthenticated = false;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      TokenService.removeToken();
    },
    setAuthenticationType: (state, action) => {
      state.authenticationType = action.payload.type;
      state.token = action.payload.token;
    },
    setUserExist: (state, action) => {
      state.isUserExist = action.payload;
    },
  },
});

export const {
  loginSuccess,
  loginFailure,
  logout,
  setAuthenticationType,
  registerSuccess,
  registerFailed,
  setUserExist,
} = authSlice.actions;

// Thunk action creator
export const login = (email, password) => async (dispatch, getState) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.LOGIN));

    const state = getState();
    const { authenticationType, token } = state.auth;

    const loginRequest = {
      authenticationType: authenticationType,
      token: token,
      email: email,
      password: password,
    };

    const response = await axios.post('/auth/login', loginRequest);

    if (response.data.success) {
      dispatch(clearNewUser());

      dispatch(
        loginSuccess({
          type: authenticationType,
          token: response.data.token,
        }),
      );

      dispatch(setCurrentUser(response.data.user));

      dispatch(setApiSuccess(ApiStatusKeys.LOGIN));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    dispatch(loginFailure());
    handleApiError(error, ApiStatusKeys.LOGIN, dispatch, 'errors.login_failed');
    throw error;
  }
};

export const forgetPassword = (email) => async (dispatch, getState) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.FORGET_PASSWORD));

    const state = getState();
    const language = state.language.lang;

    const forgetPasswordRequest = {
      email: email,
      language: language,
    };

    const response = await axios.post('/auth/reset-password-request', forgetPasswordRequest);

    if (response.data.success) {
      dispatch(setApiSuccess(ApiStatusKeys.FORGET_PASSWORD));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    const errorMessage = handleApiError(error, ApiStatusKeys.FORGET_PASSWORD, dispatch, 'errors.password_reset_failed');
    throw new Error(errorMessage);
  }
};

export const resetPassword = (token, newPassword) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.RESET_PASSWORD));

    const resetPasswordRequest = {
      token: token,
      password: newPassword,
    };

    const response = await axios.post('/auth/reset-password', resetPasswordRequest);

    if (response.data.success) {
      dispatch(setApiSuccess(ApiStatusKeys.RESET_PASSWORD));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    const errorMessage = handleApiError(error, ApiStatusKeys.RESET_PASSWORD, dispatch, 'errors.password_change_failed');
    throw new Error(errorMessage);
  }
};

export const activateUser = (email) => async (dispatch, getState) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.ACTIVATE_USER));

    const state = getState();
    const language = state.language.lang;

    const activateUserRequest = {
      email: email,
      language: language,
    };

    const response = await axios.post('/auth/activate-user', activateUserRequest);

    if (response.data.success) {
      dispatch(setApiSuccess(ApiStatusKeys.ACTIVATE_USER));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    const errorMessage = handleApiError(error, ApiStatusKeys.ACTIVATE_USER, dispatch, 'errors.activation_failed');
    throw new Error(errorMessage);
  }
};

export const verifyAccount = (token, language) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.VERIFY_ACCOUNT));

    const verifyAccountRequest = {
      token: token,
      language: language,
    };

    const response = await axios.post('/auth/verify-account', verifyAccountRequest);

    if (response.data.success) {
      dispatch(setApiSuccess(ApiStatusKeys.VERIFY_ACCOUNT));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    const errorMessage = handleApiError(error, ApiStatusKeys.VERIFY_ACCOUNT, dispatch, 'errors.verify_failed');
    throw new Error(errorMessage);
  }
};

export const handleLogout = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const { authenticationType } = state.auth;
    const token = TokenService.getToken();

    if (token) {
      if (authenticationType !== AuthenticationType.Basic) {
        await signOut();
      }

      dispatch(logout());
      dispatch(clearCurrentUser());
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.LOGOUT, dispatch, 'errors.logout_failed');
    throw error;
  }
};

// Register thunk action
export const register = (user) => async (dispatch, getState) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.REGISTER));

    const state = getState();
    const { authenticationType, token } = state.auth;
    const language = state.language.lang;

    const registerRequest = {
      authenticationType: authenticationType,
      token: token,
      user: user,
      language: language,
    };

    const response = await axios.post('/auth/register', registerRequest);

    if (response.data.success) {
      var redirectUrl = '/';

      if (authenticationType === AuthenticationType.Basic) {
        redirectUrl = '/';
      } else if (authenticationType === AuthenticationType.Facebook) {
        redirectUrl = '/profile';

        if (user.userType === UserType.Member) {
          redirectUrl = '/';
        }
      }

      dispatch(
        registerSuccess({
          type: authenticationType,
          token: response.data.token,
        }),
      );

      dispatch(setCurrentUser(response.data.user));

      dispatch(
        setApiSuccess({
          key: ApiStatusKeys.REGISTER,
          redirectUrl: redirectUrl,
        }),
      );
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.REGISTER, dispatch, 'errors.register_failed');
    throw error;
  }
};

export const isUserExist = (email) => async (dispatch) => {
  try {
    const response = await axios.post('/auth/is-user-exist', { email });

    if (response.data.success) {
      dispatch(setUserExist(response.data.success));

      return response.data;
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    const errorMessage = handleError(error, 'Kullanıcı bulunamadı');

    dispatch(setUserExist(false));

    return {
      success: false,
      errorMessage: errorMessage,
    };
  }
};

export const getRegisterToken = (email) => async (dispatch) => {
  try {
    const response = await axios.post('/auth/register-token', { email: email });

    if (response.data.success) {
      TokenService.setToken(TokenType.Register, response.data.token);
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.REGISTER_TOKEN, dispatch, 'errors.token_failed');
    throw error;
  }
};

export const getPublicToken = () => async (dispatch) => {
  try {
    const response = await axios.post('/auth/public-token');

    if (response.data.success) {
      TokenService.setToken(TokenType.Public, response.data.token);
    } else {
      throw new Error(response.data.errorMessage || 'Token alınamadı');
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.PUBLIC_TOKEN, dispatch, 'errors.token_failed');
    throw error;
  }
};

export const deleteUserAccount = (userId) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.DELETE_USER));

    const response = await axios.delete('/user/delete-user-by-id', {
      data: { Id: userId },
    });

    if (response.data.success) {
      dispatch(logout());

      dispatch(setApiSuccess(ApiStatusKeys.DELETE_USER));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    const errorMessage = handleApiError(error, ApiStatusKeys.DELETE_USER, dispatch, 'errors.user_failed');
    throw new Error(errorMessage);
  }
};

export default authSlice.reducer;
